import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowHoldAndWinOutro,
  setSceneTransitionActive,
} from "../../../State/actions";
import {
  getHoldAndWinOutroAnimations,
  formatToCustomLocale,
} from "../../../utilities";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

/**
 * HoldAndWinOutroController - Handles hold and win outro animation sequence
 * Requires manual user interaction to close (button click or overlay click)
 * Clean slate - no state dispatches except dialog visibility and critical animation blocking
 */
class HoldAndWinOutroController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.outroSpine = null;
    this.isPlaying = false;
    this.isVisible = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // Counter attached to spine bone
    this.counterContainer = null;
    this.counterBitmapTexts = [];
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;
    this.boneUpdateTicker = null;
    this.winValue = 0; // Store win value from state
    this.betValue = 0; // Store bet value from state

    // Multiplier text attached to spine bone (e.g., "32x BET")
    this.multiplierContainer = null;
    this.multiplierText = null;
    this.currentMultiplierValue = 0;
    this.multiplierAnimationRef = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;

    // User interaction state
    this.canSkipAnimation = false;
    this.hasHandledSkipClick = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;

    this.initializeOutroSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   * Matches WinAnimationController's positioning exactly
   */
  applyScaleAndCenter() {
    if (!this.outroSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers (similar to button scaling)
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    this.outroSpine.scale.set(scale);
    // Position relative to centered parent container (0,0 is now screen center)
    this.outroSpine.x = 0;
    this.outroSpine.y = -(this.symbolHeight / 2 || 0);
  }

  /**
   * Get device-specific scale multiplier for spine animations
   */
  getDeviceScaleMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 1.0;
      } else {
        return 1.0;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 1.0;
      } else {
        return 1.0;
      }
    } else {
      return 1.0;
    }
  }

  /**
   * Get device-specific font size multiplier for button text
   */
  getFontSizeMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 1.0;
      } else {
        return 1.0;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 1.0;
      } else {
        return 1.0;
      }
    } else {
      // Desktop
      return 1.0;
    }
  }

  /**
   * Get device-specific Y offset for button positioning
   * Adjusts button position based on device and orientation
   * Matches WinAnimationController, HoldAndWinIntroController, HoldAndWinReloadController, and MaxWinDialogController button offset values
   */
  getButtonYOffset() {
    const { device, orientation } = this.dimensionsContext;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 240; // Mobile portrait offset
      } else {
        return 10; // Mobile landscape offset
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return 200; // Tablet portrait offset
      } else {
        return 100; // Tablet landscape offset
      }
    } else {
      return 0; // Desktop offset
    }
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * OPTIMIZED: Only recalculate when values actually change
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed - this prevents unnecessary updates during animation start
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions; // Update the extracted dimensions object
    this.symbolHeight = symbolHeight;

    // Update button text if device changed (tap vs click)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    if (
      this.outroSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the hold and win outro spine
   */
  initializeOutroSpine() {
    try {
      this.outroSpine = this.createOutroSpine();

      if (!this.outroSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.outroSpine.skeleton) missingComponents.push("skeleton");
      if (!this.outroSpine.state) missingComponents.push("state");
      if (!this.outroSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.outroSpine);
      this.outroSpine.alpha = 0.001;
      this.outroSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.outroSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      this.warmUpOutroSpine();

      this.initializeCounter(this.winValue);
      this.initializeMultiplier();
      this.initializeButton();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create outro spine from loaded assets
   */
  createOutroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "total_win-skeleton";
      const atlasAlias = "total_win-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const outroSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!outroSpine?.skeleton?.data?.animations) {
        return null;
      }

      return outroSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  warmUpOutroSpine() {
    if (!this.outroSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.outroSpine.state.setAnimation(0, animations[0], false);
        this.outroSpine.state.update(0.016);
        this.outroSpine.skeleton.updateWorldTransform();
      }
      this.outroSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations() {
    return this.outroSpine?.skeleton?.data?.animations.map((a) => a.name) || [];
  }

  /**
   * Initialize counter and attach it to spine's "text_value_spacer" bone
   *
   * The counter is a child of the spine and follows the "text_value_spacer" bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   * The counter only manages its own text content and visibility.
   */
  initializeCounter(winValue = 0) {
    if (!this.outroSpine || !this.outroSpine.skeleton) {
      return;
    }

    // Create counter container as child of spine
    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "outro_counter_attached_to_bone";
    this.counterContainer.alpha = 0.001;
    this.counterContainer.zIndex = 1000; // Render above spine effects

    this.outroSpine.addChild(this.counterContainer);
    this.outroSpine.sortableChildren = true;

    // Initialize with win value from state
    this.updateCounterDisplay(winValue);
  }

  /**
   * Update counter display with new value
   *
   * Font size auto-scales to fit the spine's natural counter area.
   * Since counter is attached to spine bone, all position/scale/rotation comes from spine.
   *
   * @param {number} value - The value to display
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer) return;

    // Clear existing text
    this.counterBitmapTexts.forEach((text) => text.destroy());
    this.counterBitmapTexts = [];
    this.counterContainer.removeChildren();

    // Format value (currency commented out for now)
    const formattedValue = formatToCustomLocale(Math.floor(value));
    // const formattedText = `${formattedValue}$`; // Currency will be added back later
    const formattedText = formattedValue;

    // Counter area dimensions in spine's natural size (before bone scaling)
    const availableWidth = 788;
    const availableHeight = 200;

    // Start with ideal font size (45% of height - same as WinAnimationController)
    const idealFontSize = Math.round(availableHeight * 0.45);

    // Create single BitmapText with the full formatted text
    const bitmapText = new PIXI.BitmapText(formattedText, {
      fontName: "font",
      fontSize: idealFontSize,
    });

    // Center the text
    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;

    // Scale down if text exceeds 90% of available width (same as WinAnimationController)
    const textWidth = bitmapText.width;
    if (textWidth > availableWidth * 0.9) {
      const scale = (availableWidth * 0.9) / textWidth;
      bitmapText.scale.set(scale);
    }

    this.counterContainer.addChild(bitmapText);
    this.counterBitmapTexts.push(bitmapText);
  }

  /**
   * Sync counter transform with spine's "text_value_spacer" bone
   * Called every frame while spine is animating
   */
  updateCounterFromBone() {
    if (
      !this.outroSpine ||
      !this.counterContainer ||
      !this.outroSpine.skeleton
    ) {
      return;
    }

    const valueBone = this.outroSpine.skeleton.findBone("text_value_spacer");
    if (!valueBone) {
      return;
    }

    // Apply bone's world transform to counter
    this.counterContainer.x = valueBone.worldX;
    this.counterContainer.y = valueBone.worldY;
    this.counterContainer.rotation =
      (valueBone.getWorldRotationX() * Math.PI) / 180;
    this.counterContainer.scale.set(
      valueBone.getWorldScaleX(),
      valueBone.getWorldScaleY()
    );
  }

  /**
   * Initialize button and attach it to spine's "text_tap_to_contiune" bone
   *
   * The button is a child of the spine and follows the bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   */
  initializeButton() {
    if (!this.outroSpine || !this.outroSpine.skeleton) {
      console.log("[HoldAndWinOutro] Cannot initialize button - spine not ready");
      return;
    }

    const buttonBone = this.outroSpine.skeleton.findBone(
      "text_tap_to_contiune"
    );
    if (!buttonBone) {
      console.log("[HoldAndWinOutro] text_tap_to_contiune bone not found");
      return;
    }

    console.log("[HoldAndWinOutro] Initializing button on text_tap_to_contiune bone");

    // Create button container as child of spine
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "outro_continue_button_attached_to_bone";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001; // Render above counter

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Calculate font size with device-specific multiplier
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style (similar to TextButton but simpler)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize, // Scaled by device multiplier
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      align: "center",
    });

    // Create PIXI.Text
    this.buttonText = new PIXI.Text(buttonText, textStyle);
    this.buttonText.anchor.set(0.5);
    this.buttonText.x = 0;
    this.buttonText.y = 0;

    // Scale down the button (0.7x smaller)
    this.buttonText.scale.set(0.7);

    this.buttonContainer.addChild(this.buttonText);
    this.outroSpine.addChild(this.buttonContainer);
    this.outroSpine.sortableChildren = true;
  }

  /**
   * Sync button transform with spine's "text_tap_to_contiune" bone
   * Called every frame while spine is animating
   */
  updateButtonFromBone() {
    if (
      !this.outroSpine ||
      !this.buttonContainer ||
      !this.outroSpine.skeleton
    ) {
      return;
    }

    const buttonBone = this.outroSpine.skeleton.findBone(
      "text_tap_to_contiune"
    );
    if (!buttonBone) {
      console.log("[HoldAndWinOutro] text_tap_to_contiune bone not found during update");
      return;
    }

    const yOffset = this.getButtonYOffset();

    // Apply bone's world transform to button with device-specific Y offset
    this.buttonContainer.x = buttonBone.worldX;
    this.buttonContainer.y = buttonBone.worldY + yOffset;
    this.buttonContainer.rotation =
      (buttonBone.getWorldRotationX() * Math.PI) / 180;
    this.buttonContainer.scale.set(
      buttonBone.getWorldScaleX(),
      buttonBone.getWorldScaleY()
    );
  }

  /**
   * Show counter (visibility only, spine controls position/scale/rotation)
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
  }

  /**
   * Hide counter (visibility only, spine controls position/scale/rotation)
   */
  hideCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 0.001;
  }

  /**
   * Initialize multiplier text and attach it to spine's "text_x_bet_spacer" bone
   *
   * Displays the win multiplier (e.g., "32x BET")
   * The multiplier is a child of the spine and follows the bone's transform.
   */
  initializeMultiplier() {
    if (!this.outroSpine || !this.outroSpine.skeleton) {
      return;
    }

    const multiplierBone =
      this.outroSpine.skeleton.findBone("text_x_bet_spacer");
    if (!multiplierBone) {
      return;
    }

    // Create multiplier container as child of spine
    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "outro_multiplier_attached_to_bone";
    this.multiplierContainer.alpha = 0.001;
    this.multiplierContainer.zIndex = 999; // Render below counter

    // Get translated "BET" text
    const betText = this.t("bet").toUpperCase();

    // Create text style
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: 48,
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 3,
      dropShadowBlur: 3,
      dropShadowAlpha: 0.6,
      align: "center",
    });

    // Create PIXI.Text (will be updated with actual multiplier when animation starts)
    this.multiplierText = new PIXI.Text(`0x ${betText}`, textStyle);
    this.multiplierText.anchor.set(0.5);
    this.multiplierText.x = 0;
    this.multiplierText.y = 0;

    this.multiplierContainer.addChild(this.multiplierText);
    this.outroSpine.addChild(this.multiplierContainer);
    this.outroSpine.sortableChildren = true;
  }

  /**
   * Update multiplier text display
   *
   * @param {number} multiplierValue - Multiplier value to display
   */
  updateMultiplierDisplay(multiplierValue) {
    if (!this.multiplierText) return;

    const multiplier = Math.floor(multiplierValue);
    const betText = this.t("bet").toUpperCase();
    this.multiplierText.text = `${multiplier}x ${betText}`;
  }

  /**
   * Animate multiplier value from one number to another
   *
   * @param {number} fromValue - Starting multiplier value
   * @param {number} toValue - Target multiplier value
   * @param {number} duration - Animation duration in ms
   */
  startMultiplierAnimation(fromValue, toValue, duration) {
    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    this.currentMultiplierValue = fromValue;
    this.updateMultiplierDisplay(fromValue);

    this.multiplierAnimationRef = anime({
      targets: this,
      currentMultiplierValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateMultiplierDisplay(this.currentMultiplierValue);
      },
      complete: () => {
        this.currentMultiplierValue = toValue;
        this.updateMultiplierDisplay(toValue);
        this.multiplierAnimationRef = null;
      },
    });
  }

  /**
   * Instantly set multiplier to specific value (skips animation)
   *
   * @param {number} value - Multiplier value to display
   */
  jumpMultiplierToValue(value) {
    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    this.currentMultiplierValue = value;
    this.updateMultiplierDisplay(value);
  }

  /**
   * Reset multiplier to zero
   */
  resetMultiplier() {
    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    this.currentMultiplierValue = 0;
    this.updateMultiplierDisplay(0);
  }

  /**
   * Check if multiplier value is currently animating
   *
   * @returns {boolean} True if multiplier is animating
   */
  isMultiplierAnimating() {
    return this.multiplierAnimationRef !== null;
  }

  /**
   * Get current multiplier value
   *
   * @returns {number} Current multiplier value
   */
  getCurrentMultiplierValue() {
    return this.currentMultiplierValue || 0;
  }

  /**
   * Sync multiplier transform with spine's "text_x_bet_spacer" bone
   * Called every frame while spine is animating
   */
  updateMultiplierFromBone() {
    if (
      !this.outroSpine ||
      !this.multiplierContainer ||
      !this.outroSpine.skeleton
    ) {
      return;
    }

    const multiplierBone =
      this.outroSpine.skeleton.findBone("text_x_bet_spacer");
    if (!multiplierBone) {
      return;
    }

    // Apply bone's world transform to multiplier
    this.multiplierContainer.x = multiplierBone.worldX;
    this.multiplierContainer.y = multiplierBone.worldY;
    this.multiplierContainer.rotation =
      (multiplierBone.getWorldRotationX() * Math.PI) / 180;
    this.multiplierContainer.scale.set(
      multiplierBone.getWorldScaleX(),
      multiplierBone.getWorldScaleY()
    );
  }

  /**
   * Show multiplier (visibility only, spine controls position/scale/rotation)
   */
  showMultiplier() {
    if (!this.multiplierContainer) return;
    this.multiplierContainer.alpha = 1;
  }

  /**
   * Hide multiplier (visibility only, spine controls position/scale/rotation)
   */
  hideMultiplier() {
    if (!this.multiplierContainer) return;
    this.multiplierContainer.alpha = 0.001;
  }

  /**
   * Animate counter value from one number to another
   *
   * @param {number} fromValue - Starting value
   * @param {number} toValue - Target value
   * @param {number} duration - Animation duration in ms
   * @param {Function} onComplete - Callback when animation completes
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    // Start counter sound
    soundManager.play(SOUND_NAME.counter);

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;

        // Stop counter sound
        soundManager.stop(SOUND_NAME.counter);

        // Call completion callback
        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Instantly set counter to specific value (skips animation)
   *
   * @param {number} value - Value to display
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Reset counter to zero
   */
  resetCounter() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Check if counter value is currently animating
   *
   * @returns {boolean} True if counter is animating
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Get current counter value
   *
   * @returns {number} Current counter value
   */
  getCurrentCounterValue() {
    return this.currentCounterValue || 0;
  }

  /**
   * Start syncing counter, multiplier, and button with spine bone every frame
   * Uses PIXI.Ticker to update counter, multiplier, and button transforms from bones
   */
  startBoneUpdateLoop() {
    if (!this.outroSpine || !this.counterContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying && this.isVisible) {
          this.updateCounterFromBone();
          this.updateMultiplierFromBone();
          this.updateButtonFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing counter with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    // Get translated button text based on current device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Update text content
    this.buttonText.text = buttonText;

    // Recalculate font size with device-specific multiplier (matching initialization)
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Update font size
    this.buttonText.style.fontSize = finalFontSize;
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    // Hide new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Start the outro animation sequence
   * @param {number} winValue - The win value from state to display
   * @param {number} betValue - The bet value from state to calculate multiplier
   * @param {boolean} isTurboMode - If true, skip counter animation and show final values immediately
   */
  startOutroAnimation(winValue = 0, betValue = 0, isTurboMode = false) {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.outroSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isVisible = true;
      this.isWaitingForClick = false;
      this.winValue = winValue || 0;
      this.betValue = betValue || 0;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards
      this.hasHandledSkipClick = false;
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Stop feature_background_music when Hold and Win outro appears
      soundManager.fadeOut(SOUND_NAME.feature_background_music, 500);

      // Play total_win_in sound when animation starts (during IN animation)
      soundManager.play(SOUND_NAME.total_win_in);

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing (spine is always warmed up and positioned)
      if (
        this.outroSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Fade in spine smoothly
      anime({
        targets: this.outroSpine,
        alpha: [0.001, 1],
        duration: 350,
        easing: "easeOutQuad",
      });

      this.outroSpine.state.clearTracks();
      this.outroSpine.state.clearListeners();
      this.outroSpine.state.apply(this.outroSpine.skeleton);
      this.outroSpine.skeleton.setToSetupPose();

      // Reset counter and multiplier to 0 before starting animation
      this.resetCounter();
      this.resetMultiplier();

      // Update counter and multiplier positions immediately before showing to prevent flash
      this.updateCounterFromBone();
      this.updateMultiplierFromBone();

      // Show counter and multiplier, start bone update loop to follow spine animation
      this.showCounter();
      this.showMultiplier();
      this.startBoneUpdateLoop();

      // Calculate target multiplier value
      const targetMultiplier =
        this.betValue > 0 ? Math.floor(this.winValue / this.betValue) : 0;

      // Start counter and multiplier animations from 0 to target values over 3 seconds
      // In turbo mode, skip animation and show final values immediately
      if (this.winValue > 0) {
        if (isTurboMode) {
          // TURBO MODE: Skip counter animation, show final values immediately
          // Jump directly to final values
          this.jumpCounterToValue(this.winValue);
          if (this.betValue > 0 && targetMultiplier > 0) {
            this.jumpMultiplierToValue(targetMultiplier);
          }

          // Enable close functionality immediately
          this.isWaitingForClick = true;

          // Stop total_win_in, play total_win_loop briefly (no counter sound)
          soundManager.stop(SOUND_NAME.total_win_in);
          soundManager.play(SOUND_NAME.total_win_loop);
        } else {
          // NORMAL MODE: Animate counter from 0 to final value
          // Stop total_win_in and start total_win_loop when counter animation begins
          soundManager.stop(SOUND_NAME.total_win_in);
          soundManager.play(SOUND_NAME.total_win_loop);

          this.startCounterAnimation(0, this.winValue, 3000, () => {
            // Counter animation completed - enable close functionality
            if (this.isPlaying) {
              this.isWaitingForClick = true;
              // Button already shown after 1 second, just enable waiting
            }
          });

          // Start multiplier animation in parallel with counter
          if (this.betValue > 0 && targetMultiplier > 0) {
            this.startMultiplierAnimation(0, targetMultiplier, 3000);
          }
        }
      } else {
        // No win value - enable close immediately
        this.isWaitingForClick = true;
      }

      // SKIP PROTECTION: Block skipping for the first second
      // Show button after 1 second (multiplier already shown with counter)
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();

          // Enable waiting if counter already finished or no counter
          if (!this.isCounterAnimating() || this.winValue === 0) {
            this.isWaitingForClick = true;
          }
        }
      }, 1000);

      const animations = this.getAvailableAnimations();
      const { inAnimation, idleAnimation } = this.findAnimations(animations);

      if (inAnimation && idleAnimation) {
        // Play IN animation, then loop IDLE animation
        this.outroSpine.state.setAnimation(0, inAnimation, false);

        // Get IN animation duration
        const inDuration = this.getSpineAnimationDuration(inAnimation);

        // After IN completes, start looping IDLE and show button
        const inTimeout = setTimeout(() => {
          if (this.isPlaying) {
            this.outroSpine.state.setAnimation(0, idleAnimation, true);
            // Don't set isWaitingForClick yet - wait for counter to finish or be skipped
            resolve(); // Sequence ready, waiting for counter and user interaction
          }
        }, inDuration);

        // Store timeout for cleanup
        this.currentTimeout = inTimeout;
      } else if (idleAnimation) {
        // Fallback: just loop idle animation
        this.outroSpine.state.setAnimation(0, idleAnimation, true);
        resolve();
      } else {
        // No valid animations
        resolve();
      }
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.outroSpine ||
      !this.outroSpine.skeleton ||
      !this.outroSpine.skeleton.data ||
      !this.outroSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.outroSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Find appropriate animations from available list
   */
  findAnimations(animations) {
    return getHoldAndWinOutroAnimations(animations);
  }

  /**
   * Handle user click to skip counter or close animation
   *
   * Two-click logic:
   * 1. First click (during counter animation): Skips counter to final value
   * 2. Second click (after counter finished): Closes the entire animation
   *
   * Also handles spacebar input and prevents double-clicks with debouncing.
   *
   * @returns {boolean} True if click was handled, false if ignored
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.outroSpine?.state) {
      return false;
    }

    // Debounce extremely rapid inputs
    if (this.isHandlingUserClick) {
      return false;
    }

    this.isHandlingUserClick = true;

    // Clear existing debounce timeout if any
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      this.isHandlingUserClick = false;
      this.debounceTimeout = null;
    }, 300);

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      this.isHandlingUserClick = false;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }
      return false;
    }

    // Check if counter is currently animating
    const isCounterAnimating = this.isCounterAnimating();
    const currentCounterValue = this.getCurrentCounterValue();
    const isCounterFinished = currentCounterValue >= this.winValue;

    // SIMPLE LOGIC: If counter is animating, skip it; if finished, close animation
    if (isCounterAnimating && !isCounterFinished) {
      // Counter is still animating - skip to final
      this.hasHandledSkipClick = true;

      // Jump counter and multiplier to final values immediately
      this.jumpCounterToValue(this.winValue);
      const targetMultiplier =
        this.betValue > 0 ? Math.floor(this.winValue / this.betValue) : 0;
      if (targetMultiplier > 0) {
        this.jumpMultiplierToValue(targetMultiplier);
      }

      // Enable waiting for close click (button already shown after 1 second)
      this.isWaitingForClick = true;

      // Clear debounce immediately after processing to allow next click
      this.isHandlingUserClick = false;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      return true;
    } else {
      // Counter is finished or not animating - close animation
      // Only allow closing if counter was skipped or finished
      if (!this.hasHandledSkipClick && isCounterAnimating) {
        // Counter still animating but skip protection passed - allow skip
        this.hasHandledSkipClick = true;
        this.jumpCounterToValue(this.winValue);
        const targetMultiplier =
          this.betValue > 0 ? Math.floor(this.winValue / this.betValue) : 0;
        if (targetMultiplier > 0) {
          this.jumpMultiplierToValue(targetMultiplier);
        }
        this.isWaitingForClick = true;
        // Button already shown after 1 second

        this.isHandlingUserClick = false;
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
          this.debounceTimeout = null;
        }
        return true;
      }

      // Counter finished - close animation
      if (!this.isWaitingForClick) {
        // Counter finished naturally, enable close (button already shown after 1 second)
        this.isWaitingForClick = true;

        this.isHandlingUserClick = false;
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
          this.debounceTimeout = null;
        }
        return true;
      }

      // Close the animation
      this.playClosingAnimation();

      // Clear debounce immediately after processing
      this.isHandlingUserClick = false;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      return true;
    }
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.outroSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop counter and multiplier animations if still running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
      soundManager.stop(SOUND_NAME.counter);
    }

    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    // Stop total_win_in and total_win_loop, play total_win_out
    soundManager.stop(SOUND_NAME.total_win_in);
    soundManager.stop(SOUND_NAME.total_win_loop);
    soundManager.play(SOUND_NAME.total_win_out);

    // DON'T hide counter here - let it follow the spine's OUT animation
    // Counter will be hidden in finishAnimation() after spine completes
    // This allows the counter to scale down along with the spine animation

    const animations = this.getAvailableAnimations();
    const { outAnimation } = this.findAnimations(animations);

    this.outroSpine.state.clearListeners();

    if (outAnimation) {
      this.outroSpine.state.setAnimation(0, outAnimation, false);
      this.outroSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === outAnimation && this.isPlaying) {
            this.finishAnimation();
          }
        },
      });
    } else {
      // No out animation, finish immediately
      this.finishAnimation();
    }
  }

  /**
   * Finish the animation and clean up
   * Clean slate - only closes dialog and optionally re-enables critical animation blocking
   */
  finishAnimation() {
    if (!this.outroSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isVisible = false;
    this.isWaitingForClick = false;
    this.outroSpine.alpha = 0.001;

    // Stop counter and multiplier animations if running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }
    soundManager.stop(SOUND_NAME.counter);

    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    // Stop all total_win sounds
    soundManager.stop(SOUND_NAME.total_win_in);
    soundManager.stop(SOUND_NAME.total_win_loop);
    soundManager.stop(SOUND_NAME.total_win_out);

    // Stop bone update loop and hide counter and multiplier
    this.stopBoneUpdateLoop();
    this.hideCounter();
    this.hideMultiplier();

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    this.dispatch(setSceneTransitionActive(true));

    // Wait for overlay fade out to complete before removing dialog and triggering scene transition
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        // Re-enable user interface when outro finishes
        this.dispatch(setShowHoldAndWinOutro(false));
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Stop counter and multiplier animations if running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }
    soundManager.stop(SOUND_NAME.counter);

    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Cleanup counter
    if (this.counterContainer) {
      this.counterBitmapTexts.forEach((text) => text.destroy());
      this.counterBitmapTexts = [];
      if (this.counterContainer.parent) {
        this.counterContainer.parent.removeChild(this.counterContainer);
      }
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    // Cleanup multiplier
    if (this.multiplierContainer) {
      if (this.multiplierText) {
        this.multiplierText.destroy();
        this.multiplierText = null;
      }
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
    }

    if (this.outroSpine) {
      this.outroSpine.state?.clearListeners();
      this.outroSpine.state?.clearTracks();
      if (this.outroSpine.state) this.outroSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.outroSpine)) {
        this.container.removeChild(this.outroSpine);
      }
      this.outroSpine.destroy();
      this.outroSpine = null;
    }

    this.initialized = false;
  }
}

export default HoldAndWinOutroController;
